import { schoolQualifiesOpenToAll } from "../../utils/openCatchmentAccess";

/**
 * Pure function: filters a schools array based on active filter criteria.
 *
 * @param {Array}  schools   – raw schools array from API
 * @param {Object} filters   – current filter state from useFilterState
 * @param {Object} meta      – { ofstedBySchoolId, examSubjectsBySchoolId, gcseBySchoolId, ks5BySchoolId }
 * @param {Object} extra     – { homePosition } for distance calc
 * @returns {Array} filtered schools
 */

/** Synthetic catchment-type value: schools with has_catchment === false (Possible catchment). */
export const CATCHMENT_FILTER_NONE = "None";

export function applyFilters(schools, filters, meta = {}, extra = {}) {
  if (!Array.isArray(schools)) return [];

  const {
    gender = [],
    schoolType = [],
    localAuthority = [],
    catchmentCategory = [],
    ofstedRating = [],
    oxbridgeOffers = 0,
    examSubjects = [],

    gcseGrade89 = 0,
    gcseGrade79 = 0,
    attainment8 = 0,
    progress8 = -1,
    engMath5 = 0,
    ebaccEntry = 0,

    alevelAstar = 0,
    alevelAstarA = 0,
    alevelAAB = 0,
    alevelAPS = 0,
    alevelValueAdded = -1,

    distanceMiles = 20,
  } = filters;

  const {
    ofstedBySchoolId = {},
    examSubjectsBySchoolId = {},
    gcseBySchoolId = {},
    ks5BySchoolId = {},
  } = meta;

  const { homePosition } = extra;

  const noFiltersActive =
    gender.length === 0 &&
    schoolType.length === 0 &&
    localAuthority.length === 0 &&
    catchmentCategory.length === 0 &&
    ofstedRating.length === 0 &&
    oxbridgeOffers === 0 &&
    examSubjects.length === 0 &&
    gcseGrade89 === 0 &&
    gcseGrade79 === 0 &&
    attainment8 === 0 &&
    progress8 === -1 &&
    engMath5 === 0 &&
    ebaccEntry === 0 &&
    alevelAstar === 0 &&
    alevelAstarA === 0 &&
    alevelAAB === 0 &&
    alevelAPS === 0 &&
    alevelValueAdded === -1 &&
    distanceMiles === 20;

  if (noFiltersActive) return schools;

  return schools.filter((s) => {
    if (gender.length > 0 && !gender.includes(s.gender_type)) return false;

    if (schoolType.length > 0 && !schoolType.includes(s.school_type)) return false;

    if (localAuthority.length > 0 && !localAuthority.includes(s.local_authority)) return false;

    if (catchmentCategory.length > 0) {
      const wantsNone = catchmentCategory.includes(CATCHMENT_FILTER_NONE);
      const selectedCats = catchmentCategory.filter(
        (c) => c !== CATCHMENT_FILTER_NONE
      );
      const cat = String(s.catchment_category || "").trim().toLowerCase();
      // Same as left-panel Possible: no catchment flag, and not open/both
      const matchNone =
        wantsNone &&
        !s.has_catchment &&
        cat !== "open" &&
        cat !== "both";
      // Filter label "Open to All" includes Both schools with an Open seat band
      // (e.g. KEGS 30, Sutton 75) — not only catchment_category === "Open".
      const matchCat =
        selectedCats.length > 0 &&
        selectedCats.some((sel) => {
          if (sel === "Open") return schoolQualifiesOpenToAll(s);
          return s.catchment_category === sel;
        });
      if (!matchNone && !matchCat) return false;
    }

    if (ofstedRating.length > 0) {
      const grade = ofstedBySchoolId[s.id];
      if (!grade || !ofstedRating.includes(grade)) return false;
    }

    if (oxbridgeOffers > 0) {
      const count = Number(s.oxbridge_offers) || 0;
      if (count < oxbridgeOffers) return false;
    }

    if (examSubjects.length > 0) {
      const schoolSubjects = examSubjectsBySchoolId[s.id] || [];
      const hasAll = examSubjects.every((sub) => schoolSubjects.includes(sub));
      if (!hasAll) return false;
    }

    // --- GCSE filters (minimum threshold) ---
    const gcse = gcseBySchoolId[s.id];
    if (gcseGrade89 > 0) {
      if (!gcse || gcse.gcse_entries_pct_grade_8_9 == null || gcse.gcse_entries_pct_grade_8_9 < gcseGrade89) return false;
    }
    if (gcseGrade79 > 0) {
      if (!gcse || gcse.gcse_entries_pct_grade_7_9 == null || gcse.gcse_entries_pct_grade_7_9 < gcseGrade79) return false;
    }
    if (attainment8 > 0) {
      if (!gcse || gcse.attainment8_average == null || gcse.attainment8_average < attainment8) return false;
    }
    if (progress8 > -1) {
      if (!gcse || gcse.progress8_average == null || gcse.progress8_average < progress8) return false;
    }
    if (engMath5 > 0) {
      if (!gcse || gcse.engmath_95_percent == null || gcse.engmath_95_percent < engMath5) return false;
    }
    if (ebaccEntry > 0) {
      if (!gcse || gcse.ebacc_entering_percent == null || gcse.ebacc_entering_percent < ebaccEntry) return false;
    }

    // --- A Level filters (minimum threshold) ---
    const ks5 = ks5BySchoolId[s.id];
    if (alevelAstar > 0) {
      if (!ks5 || ks5.alevel_entries_pct_grade_astar == null || ks5.alevel_entries_pct_grade_astar < alevelAstar) return false;
    }
    if (alevelAstarA > 0) {
      if (!ks5 || ks5.alevel_entries_pct_grade_astar_a == null || ks5.alevel_entries_pct_grade_astar_a < alevelAstarA) return false;
    }
    if (alevelAAB > 0) {
      if (!ks5 || ks5.aab_percent == null || ks5.aab_percent < alevelAAB) return false;
    }
    if (alevelAPS > 0) {
      if (!ks5 || ks5.aps_per_entry == null || ks5.aps_per_entry < alevelAPS) return false;
    }
    if (alevelValueAdded > -1) {
      if (!ks5 || ks5.value_added == null || ks5.value_added < alevelValueAdded) return false;
    }

    // --- Distance from Home ---
    if (distanceMiles < 20 && homePosition) {
      const d = haversineDistanceMiles(homePosition[0], homePosition[1], s.lat, s.lon);
      if (d > distanceMiles) return false;
    }

    return true;
  });
}

function haversineDistanceMiles(lat1, lon1, lat2, lon2) {
  if (lat2 == null || lon2 == null) return Infinity;
  const R = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function toRad(deg) {
  return (deg * Math.PI) / 180;
}
